import { Navigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useCoreDoc } from "./useCoreDoc";
import { useDocSelector } from "../core/useDocSelector";
import { readSettings } from "./settings";
import { useDevice } from "./device";
import { listDashboards } from "../dashboards/model";
import { HomePage } from "./HomePage";
import { useSession } from "./session";
import { ensureStarterDashboard } from "../dashboards/starter";
import { useEffect } from "react";
import { useReadOnly } from "./useReadOnly";

/**
 * `/` (spec §9.7, §9.24): first run → /welcome; else the device's dashboard; else the first dashboard;
 * else the plain list. A kiosk (`?kiosk=1`) never sees the wizard.
 */
export function RootRedirect() {
  const { t } = useTranslation();
  const { handle } = useCoreDoc();
  // one selector → one consistent snapshot: `settled`, onboarding and dashboards must never be read
  // from different frames, or a returning person is sent through the wizard again
  const state = useDocSelector(handle, (doc, h) => ({
    settled: h.settled,
    onboarded: readSettings(doc).onboarded === true,
    dashboards: listDashboards(doc).map((d) => d.slug),
  }));
  const settled = state?.settled ?? false;
  const settings = { onboarded: state?.onboarded ?? false };
  const device = useDevice((s) => s.dashboard);
  const dashboards = (state?.dashboards ?? []).map((slug) => ({ slug }));
  const { search } = useLocation();
  const kiosk = new URLSearchParams(search).get("kiosk") === "1";
  const session = useSession((s) => s.session);
  const workspaceId = useSession((s) => s.workspaceId);
  const canWrite = !useReadOnly(handle, workspaceId);
  // the wizard runs once, in a profile's first (own) workspace; further workspaces just get the starter dashboard
  const first = session?.workspaces?.[0]?.id === workspaceId;
  const needsStarter = settled && canWrite && dashboards.length === 0 && (!first || settings.onboarded);
  useEffect(() => {
    if (needsStarter && handle) ensureStarterDashboard(handle.doc);
  }, [needsStarter, handle]);
  if (!settled) return <p className="p-8 text-neutral-500">{t("session.loading")}</p>;
  if (!settings.onboarded && first && !kiosk && canWrite) return <Navigate to="/welcome" replace />;
  const target = (device && dashboards.find((d) => d.slug === device)?.slug) ?? dashboards[0]?.slug;
  if (target) return <Navigate to={`/d/${target}${search}`} replace />;
  return <HomePage />;
}
